import { useState, useMemo, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { locationPageUrl } from "../data/locationData";
import { stateOutlines } from "./OurLocations";
import { riseClass, riseDelay, useReveal } from "../hooks/useReveal";

const outlineById = Object.fromEntries(stateOutlines.map((state) => [state.id, state.d]));

export const stateData = [
  {
    id: "nsw",
    label: "NSW",
    name: "New South Wales",
    regions: [
      {
        name: "Greater Sydney",
        suburbs: [
          "Alexandria", "Annandale", "Artarmon", "Ashfield", "Auburn",
          "Balmain", "Bankstown", "Baulkham Hills", "Blacktown", "Bondi Junction",
          "Botany", "Burwood", "Campbelltown", "Canterbury", "Carlingford",
          "Castle Hill", "Chatswood", "Concord", "Coogee", "Cronulla",
          "Drummoyne", "Eastwood", "Epping", "Five Dock", "Gladesville",
          "Glebe", "Homebush", "Hornsby", "Hurstville", "Kellyville",
          "Kingsford", "Kogarah", "Lane Cove", "Leichhardt", "Liverpool",
          "Macquarie Park", "Marrickville", "Mascot", "Miranda", "Neutral Bay",
          "Newtown", "North Sydney", "Paddington", "Parramatta", "Penrith",
          "Pyrmont", "Randwick", "Rhodes", "Rockdale", "Rozelle",
          "Ryde", "St Leonards", "Strathfield", "Surry Hills", "Sydney CBD",
          "Westmead", "Wolli Creek", "Zetland",
        ],
      },
      {
        name: "Western Sydney",
        suburbs: [
          "Auburn", "Blacktown", "Cabramatta", "Canley Vale", "Casula",
          "Edmondson Park", "Fairfield", "Gregory Hills", "Kellyville",
          "Leppington", "Liverpool", "Merrylands", "Mount Druitt", "Narellan",
          "Oran Park", "Parramatta", "Penrith", "Rouse Hill", "Seven Hills",
          "Wentworthville", "Westmead", "Windsor",
        ],
      },
      {
        name: "Southern Sydney",
        suburbs: [
          "Caringbah", "Cronulla", "Engadine", "Gymea", "Hurstville",
          "Kogarah", "Miranda", "Rockdale", "Sutherland", "Taren Point",
        ],
      },
      {
        name: "Northern Sydney",
        suburbs: [
          "Artarmon", "Baulkham Hills", "Chatswood", "Dee Why", "Frenchs Forest",
          "Gladesville", "Gordon", "Hornsby", "Lane Cove", "Macquarie Park",
          "Manly", "Mosman", "Neutral Bay", "North Sydney", "Pymble",
          "Ryde", "St Ives", "St Leonards", "Turramurra",
        ],
      },
      {
        name: "Northern Beaches",
        suburbs: [
          "Avalon Beach", "Balgowlah", "Brookvale", "Collaroy", "Dee Why",
          "Manly", "Mona Vale", "Narrabeen", "Newport", "Palm Beach",
          "Pittwater", "Warriewood",
        ],
      },
      {
        name: "South West Sydney",
        suburbs: [
          "Cabramatta", "Campbelltown", "Canley Vale", "Casula", "Edmondson Park",
          "Fairfield", "Gregory Hills", "Leppington", "Liverpool", "Narellan",
          "Oran Park",
        ],
      },
      {
        name: "Central Coast",
        suburbs: [
          "Bateau Bay", "Erina", "Gosford", "Hamlyn Terrace", "Kariong",
          "Terrigal", "The Entrance", "Tuggerah", "Umina Beach", "Wyong",
        ],
      },
      {
        name: "Hunter Valley",
        suburbs: [
          "Adamstown", "Belmont", "Cardiff", "Cessnock", "Charlestown",
          "Edgeworth", "Hamilton", "Kotara", "Lake Macquarie", "Maitland",
          "Merewether", "Muswellbrook", "New Lambton", "Newcastle CBD",
          "Rutherford", "Singleton", "Wallsend", "Warners Bay",
        ],
      },
      {
        name: "Blue Mountains",
        suburbs: [
          "Blackheath", "Blaxland", "Glenbrook", "Katoomba", "Lawson",
          "Leura", "Lithgow", "Mount Victoria", "Springwood", "Wentworth Falls",
        ],
      },
      {
        name: "Illawarra & Wollongong",
        suburbs: [
          "Albion Park", "Bulli", "Corrimal", "Dapto", "Fairy Meadow",
          "Figtree", "Kiama", "Nowra", "Oak Flats", "Port Kembla",
          "Shellharbour", "Thirroul", "Unanderra", "Warilla", "Wollongong CBD",
        ],
      },
      {
        name: "Regional NSW",
        suburbs: [
          "Albury", "Armidale", "Ballina", "Bathurst", "Batemans Bay",
          "Bega", "Broken Hill", "Byron Bay", "Coffs Harbour", "Cooma",
          "Dubbo", "Grafton", "Griffith", "Gunnedah", "Inverell",
          "Lismore", "Merimbula", "Moree", "Narrabri", "Orange",
          "Port Macquarie", "Tamworth", "Tweed Heads", "Wagga Wagga",
        ],
      },
    ],
  },
  {
    id: "vic",
    label: "VIC",
    name: "Victoria",
    regions: [
      {
        name: "Melbourne CBD & Inner",
        suburbs: [
          "Abbotsford", "Albert Park", "Carlton", "Collingwood", "Docklands",
          "East Melbourne", "Fitzroy", "Fitzroy North", "Flemington",
          "Kensington", "Melbourne CBD", "Middle Park", "North Melbourne",
          "Parkville", "Port Melbourne", "Prahran", "Richmond",
          "South Melbourne", "South Yarra", "Southbank", "St Kilda",
          "Toorak", "West Melbourne", "Windsor", "Yarraville",
        ],
      },
      {
        name: "Inner East Melbourne",
        suburbs: [
          "Balwyn", "Camberwell", "Canterbury", "Glen Iris", "Hawthorn",
          "Kew", "Malvern",
        ],
      },
      {
        name: "Eastern Melbourne",
        suburbs: [
          "Bayswater", "Blackburn", "Boronia", "Box Hill", "Croydon",
          "Doncaster", "Ferntree Gully", "Forest Hill", "Glen Waverley",
          "Knox", "Lilydale", "Mitcham", "Mount Waverley", "Nunawading",
          "Ringwood", "Rowville", "Templestowe", "Vermont", "Wantirna",
          "Warrandyte",
        ],
      },
      {
        name: "Western Melbourne",
        suburbs: [
          "Altona", "Caroline Springs", "Deer Park", "Footscray",
          "Hoppers Crossing", "Laverton", "Melton", "Newport",
          "Point Cook", "Seddon", "Sunshine", "Tarneit", "Truganina",
          "Werribee", "Williamstown", "Wyndham Vale",
        ],
      },
      {
        name: "Northern Melbourne",
        suburbs: [
          "Brunswick", "Bundoora", "Coburg", "Craigieburn", "Doreen",
          "Eltham", "Epping", "Greensborough", "Heidelberg", "Lalor",
          "Mernda", "Mill Park", "Northcote", "Preston", "Reservoir",
          "South Morang", "Thomastown", "Whittlesea", "Wollert",
        ],
      },
      {
        name: "Southern Melbourne",
        suburbs: [
          "Berwick", "Brighton", "Carrum Downs", "Caulfield", "Cheltenham",
          "Clayton", "Cranbourne", "Dandenong", "Frankston", "Hampton",
          "Keysborough", "Mentone", "Moorabbin", "Mornington", "Narre Warren",
          "Noble Park", "Oakleigh", "Pakenham", "Seaford", "Springvale",
        ],
      },
      {
        name: "Geelong",
        suburbs: [
          "Armstrong Creek", "Barwon Heads", "Belmont", "Clifton Springs",
          "Corio", "Drysdale", "Geelong CBD", "Grovedale", "Lara",
          "Leopold", "Newtown", "Norlane", "Ocean Grove", "Portarlington",
          "Torquay",
        ],
      },
      {
        name: "Ballarat",
        suburbs: [
          "Alfredton", "Ballarat CBD", "Ballarat East", "Ballarat North",
          "Delacombe", "Lucas", "Mount Clear", "Sebastopol", "Wendouree",
        ],
      },
      {
        name: "Bendigo",
        suburbs: [
          "Bendigo CBD", "California Gully", "Eaglehawk", "Epsom",
          "Flora Hill", "Golden Square", "Kangaroo Flat", "Kennington",
          "Long Gully", "Quarry Hill", "Spring Gully", "Strathdale",
          "White Hills",
        ],
      },
      {
        name: "Regional Victoria",
        suburbs: [
          "Bairnsdale", "Castlemaine", "Echuca", "Hamilton", "Horsham",
          "Mildura", "Morwell", "Sale", "Seymour", "Shepparton",
          "Swan Hill", "Traralgon", "Wangaratta", "Warrnambool",
          "Wodonga", "Wonthaggi",
        ],
      },
    ],
  },
  {
    id: "qld",
    label: "QLD",
    name: "Queensland",
    regions: [
      {
        name: "Greater Brisbane",
        suburbs: [
          "Auchenflower", "Bowen Hills", "Brisbane CBD", "East Brisbane",
          "Fortitude Valley", "Herston", "Highgate Hill", "Kangaroo Point",
          "Kelvin Grove", "Milton", "New Farm", "Newstead",
          "Paddington", "Petrie Terrace", "Red Hill", "South Brisbane",
          "Spring Hill", "Toowong", "West End", "Woolloongabba",
        ],
      },
      {
        name: "Northern Brisbane",
        suburbs: [
          "Aspley", "Chermside", "Clayfield", "Geebung", "Hamilton",
          "Kedron", "Nundah", "Stafford", "Windsor", "Wooloowin",
        ],
      },
      {
        name: "Southern Brisbane",
        suburbs: [
          "Algester", "Annerley", "Calamvale", "Coopers Plains", "Dutton Park",
          "Eight Mile Plains", "Fairfield", "Greenslopes", "Holland Park",
          "Macgregor", "Moorooka", "Mount Gravatt", "Runcorn", "Salisbury",
          "Slacks Creek", "Stones Corner", "Sunnybank", "Wishart",
          "Yeerongpilly", "Yeronga",
        ],
      },
      {
        name: "Logan",
        suburbs: [
          "Beenleigh", "Browns Plains", "Kingston", "Logan Central",
          "Loganholme", "Marsden", "Meadowbrook", "Springwood",
          "Underwood", "Woodridge",
        ],
      },
      {
        name: "Ipswich",
        suburbs: [
          "Booval", "Bundamba", "Goodna", "Ipswich CBD", "Leichhardt",
          "North Ipswich", "One Mile", "Redbank Plains", "Springfield", "Yamanto",
        ],
      },
      {
        name: "Eastern Brisbane & Bayside",
        suburbs: [
          "Alexandra Hills", "Balmoral", "Capalaba", "Cleveland", "Manly",
          "Mount Cotton", "Redland Bay", "Thornlands", "Victoria Point",
          "Wellington Point",
        ],
      },
      {
        name: "Gold Coast",
        suburbs: [
          "Arundel", "Biggera Waters", "Broadbeach", "Bundall", "Burleigh Heads",
          "Carrara", "Coolangatta", "Coomera", "Helensvale", "Hope Island",
          "Labrador", "Mermaid Beach", "Nerang", "Oxenford", "Palm Beach",
          "Robina", "Southport", "Surfers Paradise", "Varsity Lakes",
        ],
      },
      {
        name: "Sunshine Coast",
        suburbs: [
          "Alexandra Headland", "Bli Bli", "Buderim", "Caloundra",
          "Caloundra West", "Coolum Beach", "Forest Glen", "Kawana",
          "Maleny", "Maroochydore", "Mountain Creek", "Nambour",
          "Noosa Heads", "Palmview", "Sippy Downs", "Twin Waters",
          "Warana", "Yandina",
        ],
      },
      {
        name: "Townsville",
        suburbs: [
          "Aitkenvale", "Annandale", "Bohle Plains", "Condon", "Cranbrook",
          "Douglas", "Garbutt", "Hyde Park", "Kirwan", "Mount Louisa",
          "Mundingburra", "Pimlico", "Rasmussen", "South Townsville",
          "Thuringowa", "Townsville CBD", "Willows",
        ],
      },
      {
        name: "Cairns",
        suburbs: [
          "Bentley Park", "Brinsmead", "Cairns CBD", "Cairns North",
          "Edge Hill", "Edmonton", "Gordonvale", "Manunda", "Manoora",
          "Mount Sheridan", "Redlynch", "Smithfield", "Westcourt",
          "White Rock", "Woree",
        ],
      },
      {
        name: "Regional QLD",
        suburbs: [
          "Bowen", "Bundaberg", "Chinchilla", "Dalby", "Emerald",
          "Gladstone", "Gympie", "Hervey Bay", "Kingaroy", "Longreach",
          "Mackay", "Maryborough", "Mount Isa", "Proserpine", "Rockhampton",
          "Roma", "Stanthorpe", "Toowoomba", "Warwick", "Yeppoon",
        ],
      },
    ],
  },
  {
    id: "wa",
    label: "WA",
    name: "Western Australia",
    regions: [
      {
        name: "Perth CBD & Inner",
        suburbs: [
          "Burswood", "Crawley", "East Perth", "Fremantle", "Highgate",
          "Inglewood", "Jolimont", "Leederville", "Maylands", "Mount Hawthorn",
          "Mount Lawley", "North Perth", "Northbridge", "Perth CBD",
          "South Fremantle", "South Perth", "Victoria Park",
          "West Leederville", "West Perth",
        ],
      },
      {
        name: "Northern Perth",
        suburbs: [
          "Alkimos", "Balcatta", "Balga", "Ballajura", "Beechboro",
          "Beldon", "Butler", "Clarkson", "Dianella", "Ellenbrook",
          "Greenwood", "Heathridge", "Hillarys", "Innaloo", "Joondalup",
          "Karrinyup", "Kingsley", "Landsdale", "Midland", "Mirrabooka",
          "Morley", "Mundaring", "Nollamara", "Osborne Park", "Scarborough",
          "Stirling", "Swan", "Two Rocks", "Wangara", "Wanneroo", "Yokine",
        ],
      },
      {
        name: "Southern Perth",
        suburbs: [
          "Armadale", "Atwell", "Baldivis", "Brentwood", "Bull Creek",
          "Byford", "Canning", "Canning Vale", "Cockburn", "Gosnells",
          "Harrisdale", "Jandakot", "Kelmscott", "Kenwick", "Maddington",
          "Mandurah", "Melville", "Murdoch", "Piara Waters", "Rockingham",
          "Secret Harbour", "Southern River", "Success", "Thornlie", "Wellard",
        ],
      },
      {
        name: "Western Suburbs",
        suburbs: [
          "Applecross", "Attadale", "Bicton", "City Beach", "Claremont",
          "Cottesloe", "Dalkeith", "Floreat", "Jolimont", "Karrakatta",
          "Mosman Park", "Nedlands", "Peppermint Grove", "Shenton Park",
          "Subiaco", "Swanbourne", "Wembley",
        ],
      },
      {
        name: "Bunbury & South West",
        suburbs: [
          "Australind", "Bunbury", "Busselton", "Capel", "Collie",
          "Dunsborough", "Eaton", "Harvey", "Manjimup", "Margaret River",
          "Pemberton",
        ],
      },
      {
        name: "Goldfields",
        suburbs: [
          "Boulder", "Coolgardie", "Esperance", "Kalgoorlie", "Norseman",
        ],
      },
      {
        name: "Mid West",
        suburbs: [
          "Carnarvon", "Dongara", "Exmouth", "Geraldton", "Jurien Bay",
        ],
      },
      {
        name: "Great Southern",
        suburbs: [
          "Albany", "Denmark", "Katanning", "Mount Barker",
        ],
      },
      {
        name: "Pilbara",
        suburbs: [
          "Dampier", "Karratha", "Newman", "Paraburdoo", "Port Hedland",
          "Tom Price",
        ],
      },
      {
        name: "Kimberley",
        suburbs: [
          "Broome", "Derby", "Halls Creek", "Kununurra", "Wyndham",
        ],
      },
    ],
  },
  {
    id: "sa",
    label: "SA",
    name: "South Australia",
    regions: [
      {
        name: "Adelaide CBD & Inner",
        suburbs: [
          "Adelaide CBD", "Bowden", "Brompton", "Burnside", "College Park",
          "Gilberton", "Glenelg", "Glenside", "Goodwood", "Hackney",
          "Hazelwood Park", "Henley Beach", "Hindmarsh", "Hyde Park",
          "Kent Town", "Kensington Gardens", "Linden Park", "Malvern",
          "Marryatville", "Millswood", "Mitcham", "Norwood", "Parkside",
          "Payneham", "Plympton", "Port Adelaide", "Prospect", "Rose Park",
          "Semaphore", "Thebarton", "Toorak Gardens", "Torrensville",
          "Unley", "Wayville",
        ],
      },
      {
        name: "Northern Adelaide",
        suburbs: [
          "Andrews Farm", "Angle Vale", "Blakeview", "Craigmore", "Elizabeth",
          "Gawler", "Golden Grove", "Modbury", "Munno Para", "Para Hills",
          "Parafield", "Parafield Gardens", "Paralowie", "Playford",
          "Pooraka", "Salisbury", "Smithfield", "Tea Tree Gully",
          "Virginia", "Waterloo Corner",
        ],
      },
      {
        name: "Southern Adelaide",
        suburbs: [
          "Aldinga Beach", "Christie Downs", "Christies Beach", "Hackham",
          "Hallett Cove", "Happy Valley", "Moana", "Morphett Vale",
          "Morphettville", "Noarlunga", "Old Noarlunga", "Old Reynella",
          "Reynella", "Seaford",
        ],
      },
      {
        name: "Eastern Adelaide",
        suburbs: [
          "Athelstone", "Auldana", "Campbelltown", "Hectorville",
          "Kensington", "Magill", "Marden", "Newton", "Rostrevor",
          "Tranmere", "Wattle Park",
        ],
      },
      {
        name: "Barossa Valley",
        suburbs: [
          "Angaston", "Lyndoch", "Nuriootpa", "Tanunda", "Two Wells",
        ],
      },
      {
        name: "Fleurieu Peninsula",
        suburbs: [
          "Goolwa", "McLaren Flat", "McLaren Vale", "Middleton",
          "Port Elliot", "Strathalbyn", "Victor Harbor", "Willunga",
        ],
      },
      {
        name: "Regional SA",
        suburbs: [
          "Bordertown", "Ceduna", "Loxton", "Millicent", "Mount Gambier",
          "Murray Bridge", "Naracoorte", "Penola", "Port Augusta",
          "Port Lincoln", "Port Pirie", "Renmark", "Streaky Bay",
          "Whyalla", "Woomera",
        ],
      },
    ],
  },
  {
    id: "tas",
    label: "TAS",
    name: "Tasmania",
    regions: [
      {
        name: "Greater Hobart",
        suburbs: [
          "Battery Point", "Bellerive", "Clarence", "Claremont",
          "Glenorchy", "Hobart CBD", "Howrah", "Huonville",
          "Kingston", "Kingston Beach", "Lindisfarne", "Margate",
          "Midway Point", "Montagu Bay", "Moonah", "New Town",
          "North Hobart", "Risdon Vale", "Rokeby", "Rosny Park",
          "Sandy Bay", "Sorell", "South Hobart", "West Hobart",
        ],
      },
      {
        name: "Hobart Surrounds",
        suburbs: [
          "Cambridge", "Dodges Ferry", "New Norfolk", "Richmond",
        ],
      },
      {
        name: "Launceston & North",
        suburbs: [
          "Beaconsfield", "Beauty Point", "Deloraine", "East Launceston",
          "George Town", "Launceston CBD", "Longford", "Perth",
          "Prospect", "Ravenswood", "Scottsdale", "Sheffield",
          "South Launceston", "Westbury",
        ],
      },
      {
        name: "North West Tasmania",
        suburbs: [
          "Burnie", "Devonport", "Penguin", "Smithton",
          "Ulverstone", "Wynyard",
        ],
      },
      {
        name: "East Coast",
        suburbs: [
          "Bicheno", "Campbell Town", "Orford", "St Helens",
          "St Marys", "Swansea",
        ],
      },
      {
        name: "West Coast",
        suburbs: [
          "Queenstown", "Rosebery", "Strahan", "Zeehan",
        ],
      },
    ],
  },
  {
    id: "nt",
    label: "NT",
    name: "Northern Territory",
    regions: [
      {
        name: "Darwin Inner",
        suburbs: [
          "Bagot", "Bayview", "Casuarina", "Coconut Grove", "Darwin CBD",
          "Fannie Bay", "Jingili", "Larrakeyah", "Leanyer", "Ludmilla",
          "Millner", "Moil", "Nakara", "Nightcliff", "Parap",
          "Rapid Creek", "Stuart Park", "Tiwi", "Wagaman", "Wanguri",
          "Woolner",
        ],
      },
      {
        name: "Darwin Industrial & Outer",
        suburbs: [
          "Berrimah", "Buffalo Creek", "Gray", "Howard Springs", "Humpty Doo",
          "Karama", "Malak", "Sanderson", "Winnellie",
        ],
      },
      {
        name: "Palmerston",
        suburbs: [
          "Archer", "Bakewell", "Bellamack", "Driver", "Durack",
          "Gunn", "Johnston", "Palmerston", "Rosebery", "Woodroffe",
          "Zuccoli",
        ],
      },
      {
        name: "Alice Springs",
        suburbs: [
          "Alice Springs CBD", "Braitling", "Desert Springs", "Gillen",
          "Larapinta", "Mount Johns", "Sadadeen", "The Gap", "White Gums",
        ],
      },
      {
        name: "Katherine & Top End",
        suburbs: [
          "Jabiru", "Katherine", "Katherine East", "Mataranka",
          "Pine Creek", "Tindal",
        ],
      },
      {
        name: "Regional NT",
        suburbs: [
          "Borroloola", "Elliott", "Nhulunbuy", "Tennant Creek", "Yulara",
        ],
      },
    ],
  },
  {
    id: "act",
    label: "ACT",
    name: "Australian Capital Territory",
    regions: [
      {
        name: "Canberra CBD & Inner",
        suburbs: [
          "Acton", "Ainslie", "Barton", "Braddon", "Civic",
          "Deakin", "Dickson", "Downer", "Forrest", "Fyshwick",
          "Griffith", "Hackett", "Kingston", "Lyneham", "Manuka",
          "Narrabundah", "O'Connor", "Pialligo", "Red Hill", "Reid",
          "Russell", "Turner", "Watson", "Yarralumla",
        ],
      },
      {
        name: "Belconnen",
        suburbs: [
          "Belconnen Town Centre", "Bruce", "Charnwood", "Florey",
          "Hawker", "Latham", "Macquarie", "Page",
        ],
      },
      {
        name: "Tuggeranong",
        suburbs: [
          "Calwell", "Erindale", "Gordon", "Greenway", "Kambah",
          "Theodore", "Tuggeranong Town Centre",
        ],
      },
      {
        name: "Gungahlin",
        suburbs: [
          "Amaroo", "Bonner", "Casey", "Forde", "Franklin",
          "Gungahlin Town Centre", "Harrison", "Mitchell", "Moncrieff",
          "Ngunnawal", "Taylor",
        ],
      },
      {
        name: "Woden Valley",
        suburbs: [
          "Chifley", "Curtin", "Farrer", "Garran", "Hughes",
          "Isaacs", "Mawson", "Pearce", "Philip", "Torrens", "Woden",
        ],
      },
      {
        name: "Weston Creek",
        suburbs: [
          "Chapman", "Duffy", "Fisher", "Holder", "Rivett", "Stirling",
          "Waramanga", "Weston",
        ],
      },
      {
        name: "Queanbeyan & Border",
        suburbs: [
          "Hume", "Queanbeyan",
        ],
      },
    ],
  },
];

export default function LocationsSection() {
  const [activeState, setActiveState] = useState(stateData[0].id);
  const [activeRegion, setActiveRegion] = useState(stateData[0].regions[0].name);
  const [search, setSearch] = useState("");
  const [searchParams] = useSearchParams();
  const [sectionRef, started] = useReveal();

  // When a state param is passed via URL (e.g. ?state=vic#locations), activate it and scroll
  useEffect(() => {
    const stateParam = searchParams.get("state");
    if (!stateParam) return;
    const match = stateData.find((s) => s.id === stateParam.toLowerCase());
    if (match) {
      setActiveState(match.id);
      setActiveRegion(match.regions[0].name);
      setSearch("");
      // Small delay so the page has rendered before scrolling
      setTimeout(() => {
        sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 150);
    }
  }, [searchParams, sectionRef]);

  const currentState = useMemo(
    () => stateData.find((s) => s.id === activeState),
    [activeState]
  );

  const currentRegion = useMemo(
    () => currentState?.regions.find((r) => r.name === activeRegion) ?? currentState?.regions[0],
    [currentState, activeRegion]
  );

  function switchState(id) {
    setActiveState(id);
    const st = stateData.find((s) => s.id === id);
    setActiveRegion(st?.regions[0]?.name ?? "");
    setSearch("");
  }

  // Search: flatten all suburbs in selected state
  const searchResults = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q || !currentState) return null;
    return currentState.regions
      .flatMap((r) => r.suburbs.map((sub) => ({ suburb: sub, region: r.name })))
      .filter(({ suburb }) => suburb.toLowerCase().includes(q));
  }, [search, currentState]);

  return (
    <section
      id="locations"
      ref={sectionRef}
      className="scroll-mt-28 bg-white py-20"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-10 text-center">
          <p className={`mb-3 text-xs font-bold uppercase tracking-[0.18em] text-orange-500 ${riseClass(started)}`}>
            Where we operate
          </p>
          <h2 className="text-3xl font-extrabold uppercase tracking-tight text-black sm:text-4xl lg:text-5xl">
            <span className={`inline-block ${riseClass(started)}`} style={riseDelay(started, 140)}>
              Traffic Plans
            </span>
            <br className="hidden sm:block" />{" "}
            <span className={`inline-block ${riseClass(started)}`} style={riseDelay(started, 280)}>
              Across Australia
            </span>
          </h2>
          <p
            className={`mx-auto mt-4 max-w-2xl text-[15px] leading-relaxed text-gray-500 ${riseClass(started)}`}
            style={riseDelay(started, 400)}
          >
            Wherever your project is in Australia, Focus Traffic Plans has you
            covered. Our experienced team services metro, regional and rural
            areas nationwide.
          </p>
        </div>

        {/* State tabs */}
        <div className="mb-6 flex flex-wrap justify-center gap-2">
          {stateData.map((s, index) => (
            <div
              key={s.id}
              className={riseClass(started)}
              style={riseDelay(started, 520 + index * 60)}
            >
            <button
              type="button"
              onClick={() => switchState(s.id)}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-all ${
                activeState === s.id
                  ? "bg-orange-500 text-white shadow-md shadow-orange-500/30"
                  : "border border-gray-200 bg-white text-gray-600 hover:border-orange-400 hover:text-orange-500"
              }`}
            >
              <svg viewBox="0 0 100 100" className="h-4 w-4 shrink-0" aria-hidden>
                <path d={outlineById[s.id]} fill="currentColor" />
              </svg>
              {s.label}
            </button>
            </div>
          ))}
        </div>

        {/* Search */}
        <div className={`relative mx-auto mb-8 max-w-lg ${riseClass(started)}`} style={riseDelay(started, 980)}>
          <svg
            className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-4.35-4.35M17 11A6 6 0 105 11a6 6 0 0012 0z"
            />
          </svg>
          <input
            type="text"
            placeholder={`Search any suburb or area in ${currentState?.name ?? ""}…`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-2xl border border-gray-200 bg-white py-3.5 pl-12 pr-5 text-sm text-gray-800 shadow-sm outline-none ring-0 transition placeholder:text-gray-400 focus:border-orange-400 focus:ring-2 focus:ring-orange-400/20"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              aria-label="Clear search"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Search results */}
        <div className={riseClass(started)} style={riseDelay(started, 1100)}>
        {searchResults !== null ? (
          <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
            {searchResults.length === 0 ? (
              <p className="text-center text-sm text-gray-400">
                No results for &ldquo;{search}&rdquo; in {currentState?.name}.
              </p>
            ) : (
              <>
                <p className="mb-4 text-xs font-semibold uppercase tracking-widest text-gray-400">
                  {searchResults.length} result{searchResults.length !== 1 ? "s" : ""} in {currentState?.name}
                </p>
                <div className="grid grid-cols-2 gap-x-6 gap-y-1 sm:grid-cols-3 lg:grid-cols-4">
                  {searchResults.map(({ suburb, region }) => (
                    <div key={suburb + region}>
                      <Link
                        to={locationPageUrl(suburb) ?? "/contact"}
                        className="block truncate py-1 text-sm font-medium text-orange-500 hover:underline"
                      >
                        {suburb}
                      </Link>
                      <span className="block text-[11px] text-gray-400">{region}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        ) : (
          /* Main panel */
          <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
            <div className="grid md:grid-cols-[220px_1fr]">
              {/* Sidebar — regions */}
              <aside className="border-b border-gray-200 md:border-b-0 md:border-r">
                <div className="border-b border-gray-200 px-5 py-4">
                  <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-gray-400">
                    Regions we service
                  </p>
                </div>
                <nav className="flex flex-row flex-wrap gap-1 p-3 md:flex-col md:gap-0 md:p-0">
                  {currentState?.regions.map((r) => (
                    <button
                      key={r.name}
                      onClick={() => setActiveRegion(r.name)}
                      className={`rounded-lg px-4 py-2.5 text-left text-sm font-medium transition-all md:rounded-none md:border-l-2 ${
                        activeRegion === r.name
                          ? "border-orange-500 bg-orange-50 text-orange-600 md:bg-orange-50"
                          : "border-transparent text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                      }`}
                    >
                      {r.name}
                    </button>
                  ))}
                </nav>
              </aside>

              {/* Suburb grid */}
              <main className="p-6 sm:p-8">
                <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
                  <h3 className="text-lg font-extrabold uppercase tracking-wider text-orange-500">
                    {currentRegion?.name}
                  </h3>
                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-1.5 rounded-lg bg-orange-500 px-4 py-2 text-xs font-semibold text-white transition hover:bg-orange-600"
                  >
                    Get a quote
                    <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                </div>

                <div className="grid grid-cols-2 gap-x-4 gap-y-0 sm:grid-cols-3 lg:grid-cols-4">
                  {currentRegion?.suburbs.map((suburb) => {
                    const pageUrl = locationPageUrl(suburb);
                    return (
                      <Link
                        key={suburb}
                        to={pageUrl ?? "/contact"}
                        className={`border-b border-gray-100 py-2.5 text-[13px] font-medium transition hover:underline ${
                          pageUrl
                            ? "text-orange-500 hover:text-orange-600"
                            : "text-gray-500 hover:text-orange-500"
                        }`}
                      >
                        {suburb}
                        {pageUrl && (
                          <svg className="ml-1 inline h-3 w-3 opacity-60" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        )}
                      </Link>
                    );
                  })}
                </div>

                <p className="mt-6 text-[12px] text-gray-400">
                  Don&apos;t see your suburb?{" "}
                  <Link to="/contact" className="font-medium text-orange-500 underline">
                    Get in touch
                  </Link>{" "}
                  — we cover all of {currentState?.name}.
                </p>
              </main>
            </div>
          </div>
        )}
        </div>
      </div>
    </section>
  );
}
